<template>
  <template v-if="showNewRecipientModal">
    <Teleport to="#modal">
      <TheModal
        title="Creating new recipient"
        @closeModal="closeModal"
        :open="showNewRecipientModal"
        :showModalHeader="true"
        :closeOnClickOutside="false"
      >
        <NewRecipientForm
          @closeModal="closeModal"
          @handleNewRecipient="handleNewContact"
        ></NewRecipientForm>
      </TheModal>
    </Teleport>
  </template>
  <template v-if="showRecipientDetailsModal">
    <Teleport to="#modal">
      <WideModal
        title="Recipient details"
        @closeModal="closeModal"
        :open="showRecipientDetailsModal"
        :showModalHeader="true"
        :closeOnClickOutside="false"
      >
        <EditRecipientForm
          :campaignView="campaignView"
          :dashboardView="dashboardView"
          @closeModal="closeModal"
          @handleSuccess="handleSuccess()"
          :recipient="selectedRecipient"
        ></EditRecipientForm>
      </WideModal>
    </Teleport>
  </template>
  <template v-if="showDeleteModal || showRemoveModal">
    <Teleport to="#modal">
      <TheModal
        :showModalHeader="true"
        :showModalBody="true"
        :closeOnClickOutside="false"
        :title="
          showDeleteModal
            ? 'Delete selected recipients'
            : 'Remove selected recipients'
        "
        @closeModal="closeModal"
        :open="showDeleteModal || showRemoveModal"
      >
        <ModalBody>
          <template v-if="showDeleteModal"
            ><DeleteData
              :recipients="idsToDelete"
              @closeModal="closeModal"
              @confirmDelete="confirmDelete()"
            ></DeleteData
          ></template>
          <template v-if="showRemoveModal"
            ><DeleteData
              :groupRecipients="idsToRemove"
              @closeModal="closeModal"
              @confirmRemove="confirmRemove()"
            ></DeleteData
          ></template>
        </ModalBody>
      </TheModal>
    </Teleport>
  </template>
  <template v-if="showNewGroupModal">
    <Teleport to="#modal">
      <TheModal
        title="Creating group"
        subheading="Give your group a name and then we'll add recipients to it."
        @closeModal="closeModal"
        :open="showNewGroupModal"
        :showModalHeader="true"
        :closeOnClickOutside="false"
      >
        <GroupForm
          @closeModal="closeModal"
          :members="selectedRecipients"
        ></GroupForm>
      </TheModal>
    </Teleport>
  </template>
  <template v-if="showBulkEditModal">
    <Teleport to="#modal">
      <TheModal
        :showModalHeader="true"
        :showModalBody="true"
        :closeOnClickOutside="false"
        title="Adding recipients to existing group"
        @closeModal="closeModal"
        :open="showBulkEditModal"
      >
        <ModalBody>
          <BulkEditData
            :groupRecipients="recipientIdsToEdit"
            @closeModal="closeModal"
            @confirmBulkEdit="updateGroupMembers"
          ></BulkEditData>
        </ModalBody>
      </TheModal>
    </Teleport>
  </template>
  <template v-if="checkingIfUserHasRecipients">
    <div
      class="tw-flex tw-justify-center tw-items-center tw-w-full tw-max-w-5xl tw-mx-auto"
    >
      <PageSection :fullWidth="true">
        <PulseAnimation>
          <DataTableSkeleton></DataTableSkeleton>
        </PulseAnimation>
      </PageSection>
    </div>
  </template>
  <template v-else>
    <TheCard class="tw-w-full">
      <template v-if="userHasRecipients">
        <CardHeader
          :title="cardHeaderTitle"
          :showTitle="dashboardView ? true : false"
          :showActions="true"
          :useSearchBar="!dashboardView && userHasRecipients ? true : false"
        >
          <template #card-search-bar>
            <div class="tw-max-w-xl tw-w-full">
              <div class="tw-relative tw-rounded-md tw-shadow-sm tw-w-full">
                <div
                  class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3"
                >
                  <b-icon-search
                    class="tw-h-5 tw-w-5 tw-text-gray-400"
                    aria-hidden="true"
                  ></b-icon-search>
                </div>
                <input
                  @keyup.enter="handleSearchQuery()"
                  v-model="searchQuery.value"
                  type="text"
                  name="recipientSearch"
                  id="recipientSearch"
                  class="tw-block tw-w-full tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1.5 tw-pl-10 tw-text-gray-900 tw-ring-1 tw-ring-inset tw-ring-gray-300 placeholder:tw-text-slate-900 focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-blue-600 tw-text-sm tw-leading-6"
                  placeholder="Search recipient by name or email or tag"
                />
                <template v-if="searchQuery.active">
                  <div
                    @click="resetFilters()"
                    class="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3 tw-cursor-pointer"
                  >
                    <span>reset</span>
                  </div>
                </template>
              </div>
            </div>
          </template>
          <template #card-header-actions>
            <template v-if="dashboardView">
              <TheLink to="/recipients" :isRouterLink="true">
                <template #text>View all recipients</template>
              </TheLink>
            </template>
            <template v-else-if="campaignView && userHasRecipients">
              <div class="tw-flex tw-gap-2 tw-items-center">
                <ButtonDropdown
                  :disabled="selectedRecipients.length > 0"
                  :id="'segment-criteria-dropdown'"
                  :items="addOrImportDropdownItems()"
                  buttonText="Add recipient"
                  :alignLeft="false"
                  :active="false"
                  :hasLeadingIcon="true"
                  :hasTrailingIcon="false"
                  size="small"
                  variant="tertiary"
                />
                <RecipientsGroupsButtonDropdown
                  :alignLeft="false"
                  :disabled="selectedRecipients.length > 0"
                  :group="selectedGroup"
                  @handleGroupSelection="handleGroupSelection"
                ></RecipientsGroupsButtonDropdown>
                <template
                  v-if="selectedGroup && selectedRecipients.length === 0"
                >
                  <TheLink variant="critical" @click="clearGroupSelection()">
                    <template #text> clear </template>
                  </TheLink>
                </template>
              </div>
            </template>
            <template
              v-else-if="
                !dashboardView && !recipientsGroupDetailsView && !campaignView
              "
            >
              <template v-if="isBoldXchangeAdmin">
                <div class="tw-flex tw-bg-gray-100 tw-p-0.5 tw-rounded-md">
                  <TheButton
                    type="button"
                    :variant="
                      viewMyRecordsOnly === false ? 'secondary' : 'ghost'
                    "
                    size="regular"
                    @click="setRecordsUserRecordsToView(false)"
                    :hasLeadingIcon="true"
                  >
                    <template #leading-icon>
                      <b-icon-people></b-icon-people>
                    </template>
                    <template #text>Customer recipients</template>
                  </TheButton>
                  <TheButton
                    type="button"
                    :variant="
                      viewMyRecordsOnly === true ? 'secondary' : 'ghost'
                    "
                    size="regular"
                    @click="setRecordsUserRecordsToView(true)"
                    :hasLeadingIcon="true"
                  >
                    <template #leading-icon>
                      <b-icon-person></b-icon-person>
                    </template>
                    <template #text>My recipients</template>
                  </TheButton>
                </div>
              </template>
            </template>
          </template>
        </CardHeader>
      </template>
      <CardBody>
        <template v-if="recipientsAreLoading">
          <PulseAnimation>
            <DataTableSkeleton></DataTableSkeleton>
          </PulseAnimation>
        </template>
        <template v-else>
          <!-- if userHasRecipients show recipient list -->
          <template v-if="userHasRecipients && count">
            <div
              class="tw-overflow-y-auto tw-min-h-[500px] tw-max-h-[500px] tw-w-full"
            >
              <template v-if="!dashboardView">
                <div
                  class="tw-flex tw-justify-between gap-4 tw-h-12 tw-px-3 tw-bg-stone-100 tw-sticky tw-top-0 tw-z-40 tw-items-center tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-b tw-border-stone-300"
                >
                  <div>
                    <template v-if="isBoldXchangeAdmin && !viewMyRecordsOnly">
                      <p class="tw-m-0 tw-text-sm tw-font-bold tw-text-red-600">
                        You are currently viewing customer recipients
                      </p>
                    </template>
                    <template v-if="viewMyRecordsOnly">
                      <input
                        id="selectAllRecipients"
                        name="selectAllRecipients"
                        type="checkbox"
                        class="tw-left-4 tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-blue-600 focus:tw-ring-blue-600 tw-me-2"
                        :checked="
                          selectedRecipients.length === recipients.length
                        "
                        @change="selectAllRecipients($event.target.checked)"
                      /><label
                        class="tw-text-gray-900"
                        for="selectAllRecipients"
                        >Select all visible
                        <template v-if="selectedRecipients.length > 0"
                          ><span
                            class="tw-m-0 tw-text-sm tw-font-bold tw-text-blue-600 tw-ml-1"
                          >
                            ({{ selectedRecipients.length }} selected)
                          </span></template
                        ></label
                      >
                    </template>
                  </div>
                  <template
                    v-if="
                      selectedRecipients.length > 0 &&
                      !campaignView &&
                      viewMyRecordsOnly
                    "
                  >
                    <div class="tw-flex tw-gap-4 tw-items-center">
                      <!-- <DropdownMenu
                          :disabled="selectedRecipients.length === 0"
                          :items="bulkDropdownItems()"
                          :buttonDropdown="true"
                          :buttonDropdownList="true"
                          buttonText="Bulk actions"
                          :hasTrailingIcon="true"
                        >
                        </DropdownMenu> -->
                      <ButtonDropdown
                        :id="'bulk-recipients-table-actions-dropdown'"
                        :items="bulkDropdownItems()"
                        buttonText="Bulk actions"
                        :disabled="false"
                        :active="false"
                        :hasLeadingIcon="false"
                        :hasTrailingIcon="true"
                        size="small"
                        variant="secondary"
                      />
                    </div>
                  </template>
                  <template
                    v-if="selectedRecipients.length > 0 && campaignView"
                  >
                    <div class="tw-flex tw-gap-4 tw-items-center">
                      <template v-if="campaignRecipientsSelectionUpdated">
                        <p
                          class="tw-m-0 tw-text-sm tw-font-bold tw-text-green-600"
                        >
                          <b-icon-check-lg></b-icon-check-lg> Recipient list
                          updated
                        </p>
                      </template>
                      <template v-else>
                        <p
                          class="tw-m-0 tw-text-sm tw-font-bold tw-text-blue-600"
                        >
                          {{ selectedRecipients.length }} selected
                        </p>
                      </template>
                      <TheButton
                        :disabled="this.selectedRecipients.length === 0"
                        variant="success"
                        @click="setCampaignRecipients()"
                        size="small"
                        :hasLeadingIcon="true"
                      >
                        <template #leading-icon
                          ><b-icon-check-lg></b-icon-check-lg
                        ></template>
                        <template #text>Add selected recipients</template>
                      </TheButton>
                    </div>
                  </template>
                </div>
              </template>
              <table class="tw-min-w-full tw-divide-stone-300">
                <thead
                  class="tw-sticky tw-z-10 tw-border-b tw-border-stone-300"
                  :class="{ 'tw-top-12': !dashboardView }"
                >
                  <tr>
                    <th
                      scope="col"
                      class="tw-min-w-20 tw-py-3.5 tw-px-3 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      class="tw-hidden sm:tw-table-cell tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                    >
                      Email
                    </th>
                    <template v-if="!dashboardView">
                      <th
                        scope="col"
                        class="tw-hidden sm:tw-table-cell tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                      >
                        Phone number
                      </th>
                      <th
                        scope="col"
                        class="tw-hidden sm:tw-table-cell tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                      >
                        Company
                      </th>
                    </template>
                    <th
                      scope="col"
                      class="tw-px-3 tw-py-3.5 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                    >
                      Tags
                    </th>
                    <template v-if="!dashboardView">
                      <th
                        scope="col"
                        class="tw-px-3 tw-py-3.5 tw-text-sm tw-font-semibold tw-text-gray-900 tw-sticky tw-top-0 tw-border-b tw-border-gray-300 tw-bg-stone-200"
                      >
                        <span class="tw-sr-only">Edit</span>
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody
                  class="tw-divide-y tw-divide-gray-200 tw-bg-white tw-min-h-[500px]"
                >
                  <tr
                    v-for="recipient in recipientData"
                    :key="recipient.id"
                    :class="[
                      selectedRecipients.includes(recipient.id) && 'bg-gray-50',
                    ]"
                  >
                    <template v-if="isBoldXchangeAdmin && !viewMyRecordsOnly">
                      <td class="tw-px-3 tw-py-4">
                        <div class="tw-flex tw-items-center gap-2">
                          <TheLink
                            variant="tertiary"
                            @click="toggleRecipientDetailsModal(recipient)"
                          >
                            <template #text>
                              <template
                                v-if="
                                  recipient?.firstName && recipient?.lastName
                                "
                              >
                                {{
                                  recipient?.firstName +
                                  " " +
                                  recipient?.lastName
                                }}
                              </template>
                              <template v-else>N/A</template>
                            </template>
                            <template #leading-icon
                              ><b-icon-plus-lg></b-icon-plus-lg>
                            </template>
                          </TheLink>
                          <div class="sm:tw-hidden tw-flex tw-flex-col">
                            <p class="tw-p-0 tw-m-0 tw-text-sm">
                              {{ recipient?.contact?.email || "---" }}
                            </p>
                          </div>
                        </div>
                      </td>
                    </template>
                    <template v-if="viewMyRecordsOnly">
                      <td class="tw-relative tw-px-3 tw-py-4">
                        <div
                          v-if="isRecipientSelected(recipient.id)"
                          class="tw-absolute tw-inset-y-0 tw-left-0 tw-w-0.5 tw-bg-blue-600"
                        ></div>

                        <div class="tw-flex tw-items-center gap-2">
                          <template v-if="!dashboardView && !segmentId">
                            <input
                              :disabled="!viewMyRecordsOnly"
                              type="checkbox"
                              class="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-blue-600 focus:tw-ring-blue-600 tw-cursor-pointer"
                              :checked="isRecipientSelected(recipient.id)"
                              @change="toggleRecipientSelection(recipient)"
                            />
                          </template>
                          <TheLink
                            variant="tertiary"
                            @click="toggleRecipientDetailsModal(recipient)"
                          >
                            <template #text>
                              <template
                                v-if="
                                  recipient?.firstName && recipient?.lastName
                                "
                              >
                                {{
                                  recipient?.firstName +
                                  " " +
                                  recipient?.lastName
                                }}
                              </template>
                              <template v-else>N/A</template>
                            </template>
                            <template #leading-icon
                              ><b-icon-plus-lg></b-icon-plus-lg>
                            </template>
                          </TheLink>
                          <div class="sm:tw-hidden tw-flex tw-flex-col">
                            <p class="tw-p-0 tw-m-0 tw-text-sm">
                              {{ recipient?.contact?.email || "---" }}
                            </p>
                          </div>
                        </div>
                      </td>
                    </template>
                    <td
                      class="tw-hidden sm:tw-table-cell tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500"
                    >
                      {{ recipient?.contact?.email || "---" }}
                    </td>

                    <template v-if="!dashboardView">
                      <td
                        class="tw-hidden sm:tw-table-cell tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500"
                      >
                        {{ recipient?.contact?.phone || "---" }}
                      </td>
                      <td
                        class="tw-hidden sm:tw-table-cell tw-whitespace-nowrap tw-px-3 tw-py-4 tw-text-sm tw-text-gray-500"
                      >
                        {{ recipient?.contact?.company || "---" }}
                      </td>
                    </template>

                    <td
                      class="tw-hidden sm:tw-table-cell tw-px-3 tw-py-4 tw-capitalize tw-text-sm tw-text-gray-500"
                    >
                      <template
                        v-if="
                          typeof recipient?.contact?.metadata
                            ?.lifecycleStage === 'string'
                        "
                      >
                        <template
                          v-if="recipient?.contact?.metadata?.lifecycleStage"
                        >
                          {{ recipient?.contact?.metadata?.lifecycleStage }}
                        </template>
                        <template v-else> --- </template>
                      </template>

                      <template
                        v-else-if="
                          Array.isArray(
                            recipient?.contact?.metadata?.lifecycleStage
                          )
                        "
                      >
                        <template
                          v-if="
                            recipient?.contact?.metadata?.lifecycleStage
                              .length === 1
                          "
                        >
                          {{ recipient?.contact?.metadata?.lifecycleStage[0] }}
                        </template>
                        <template
                          v-else-if="
                            recipient?.contact?.metadata?.lifecycleStage
                              .length > 1
                          "
                        >
                          <div class="tw-flex tw-items-center tw-gap-2">
                            <span
                              class="tw-rounded-md tw-bg-gray-100 tw-px-1.5 tw-py-1 tw-cursor-pointer"
                            >
                              <TheTooltip
                                :toolTip="true"
                                :tooltipContent="tags(recipient)"
                              >
                                <template #icon>
                                  <div
                                    class="tw-flex tw-items-center tw-gap-2 tw-text-gray-800"
                                  >
                                    <span>Multiple </span>
                                    <b-icon-eye class="tw-z-0"></b-icon-eye>
                                  </div>
                                </template>
                              </TheTooltip>
                            </span>
                          </div>
                        </template>
                        <template v-else> --- </template>
                      </template>

                      <template v-else> --- </template>
                    </td>
                    <template v-if="!dashboardView">
                      <td
                        class="tw-py-4 tw-pl-3 tw-pr-4 tw-text-right tw-text-sm tw-font-medium sm:tw-pr-3"
                      >
                        <div class="tw-flex tw-justify-end">
                          <template v-if="!campaignView && !segmentId">
                            <template v-if="viewMyRecordsOnly">
                              <div class="tw-flex tw-justify-end">
                                <IconDropdown
                                  :id="recipient._id"
                                  :items="getDropdownItems(recipient)"
                                  :disabled="false"
                                  variant="tertiary"
                                >
                                  <template #icon>
                                    <b-icon-three-dots-vertical />
                                  </template>
                                </IconDropdown>
                              </div>
                            </template>
                            <!-- <template v-else>FALSE</template> -->
                          </template>
                        </div>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
          <!-- if userHasRecipients AND query count is 0 show recipient message -->
          <template v-if="userHasRecipients && !count">
            <div
              class="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[500px] tw-min-w-96 tw-p-10"
            >
              <template v-if="searchQuery.active">
                <img
                  style="max-height: 150px; width: auto"
                  src="../../assets/svg/illustrations-test/14_no_query_results.png"
                  alt="Image Description"
                />
              </template>
              <template v-else>
                <img
                  style="max-height: 150px; width: auto"
                  src="../../assets/svg/illustrations-test/46_blank_page.png"
                  alt="Image Description"
                />
              </template>

              <template v-if="searchQuery.active">
                <h1 class="tw-text-2xl tw-m-0">No exact matches found</h1>
                <p class="tw-m-0 tw-text-lg">
                  Try updating your search/filters or
                  <a
                    class="tw-font-bold tw-cursor-pointer"
                    @click="resetFilters()"
                    >click here</a
                  >
                  to reset.
                </p>
              </template>
              <template v-else-if="!searchQuery.active">
                <h1 class="tw-text-2xl tw-m-0">
                  Your
                  <span class="tw-text-indigo-600 tw-capitalize">{{
                    `${selectedGroup?.metadata?.name}s`
                  }}</span>
                  group is empty
                </h1>
                <p class="tw-m-0 tw-text-lg">
                  Once you add recipients to this group, they'll show up here.
                </p>
              </template>
              <template v-else>
                <h1 class="tw-text-2xl tw-m-0">No exact matches found</h1>
                <p class="tw-m-0 tw-text-lg">
                  Try updating your search/filters or
                  <a
                    class="tw-font-bold tw-cursor-pointer"
                    @click="resetFilters()"
                    >click here</a
                  >
                  to reset.
                </p>
              </template>
            </div>
          </template>
          <!-- if !userHasRecipients show message -->
          <template v-if="!userHasRecipients">
            <!-- <div
              class="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-[500px]"
            > -->
            <PageSection class="tw-w-full tw-max-w-3xl tw-mx-auto">
              <!-- <TheCard class="tw-w-full"> -->
              <TheBox :withPadding="true">
                <template v-if="groupId">
                  <EmptyState emptyState="groupRecipients"></EmptyState>
                  <BlockStack :gap="400" class="tw-mx-auto tw-max-w-xs">
                    <TheButton
                      type="button"
                      :hasLeadingIcon="true"
                      :hasTrailingIcon="true"
                      variant="tertiary"
                      size="large"
                      @click="toggleRecipientsImportModal()"
                    >
                      <template #text>
                        <div
                          class="tw-flex tw-items-center tw-justify-between tw-grow"
                        >
                          <span>Import CSV</span>
                          <b-icon-chevron-right></b-icon-chevron-right>
                        </div>
                      </template>
                      <template #leading-icon
                        ><b-icon-upload></b-icon-upload
                      ></template>
                    </TheButton>
                    <TheButton
                      type="button"
                      :hasLeadingIcon="true"
                      :hasTrailingIcon="true"
                      variant="tertiary"
                      size="large"
                      @click="toggleNewRecipientModal"
                    >
                      <template #text>
                        <div
                          class="tw-flex tw-items-center tw-justify-between tw-grow"
                        >
                          <span>Create new recipient</span>
                          <b-icon-chevron-right></b-icon-chevron-right>
                        </div>
                      </template>
                      <template #leading-icon
                        ><b-icon-plus-lg></b-icon-plus-lg
                      ></template>
                    </TheButton>
                  </BlockStack>
                </template>
                <template v-else>
                  <EmptyState emptyState="recipients"></EmptyState>
                  <div class="tw-flex tw-flex-col tw-gap-4 tw-items-center">
                    <TheButton
                      type="button"
                      :hasLeadingIcon="true"
                      variant="primary"
                      size="large"
                      @click="toggleNewRecipientModal"
                    >
                      <template #text>Add recipient</template>
                      <template #leading-icon
                        ><b-icon-plus-lg></b-icon-plus-lg
                      ></template>
                    </TheButton>
                    <TheLink
                      type="button"
                      variant="tertiary"
                      size="large"
                      :isRouterLink="true"
                      to="/recipients/import"
                    >
                      <template #text>Import contacts</template>
                    </TheLink>
                  </div>
                </template>
              </TheBox>
              <!-- </TheCard> -->
            </PageSection>
            <!-- </div> -->
          </template>
        </template>
      </CardBody>
      <template
        v-if="userHasRecipients && !dashboardView && !viewLifecycleDistribution"
      >
        <CardFooter>
          <!-- Pagination -->
          <nav
            class="tw-flex tw-items-center tw-justify-between tw-border-t tw-border-gray-200"
            aria-label="Pagination"
          >
            <div class="tw-hidden sm:tw-block">
              <p class="tw-text-sm tw-text-gray-700 tw-m-0">
                Showing
                <span class="tw-font-bold"
                  >{{ this.recipientData.length }} of {{ count }}</span
                >
                records
              </p>
            </div>
            <div
              class="tw-flex tw-flex-1 tw-justify-between tw-gap-x-3 sm:tw-justify-end"
            >
              <TheButton
                class="disabled:tw-bg-slate-200 disabled:tw-cursor-auto"
                :disabled="
                  this.currentPage === 1 || this.selectedRecipients.length > 0
                "
                @click="previousPage()"
                variant="tertiary"
                size="small"
              >
                <template #text
                  ><b-icon-chevron-left></b-icon-chevron-left
                ></template>
              </TheButton>
              <TheButton
                class="disabled:tw-bg-slate-200"
                :disabled="
                  this.isLastPage || this.selectedRecipients.length > 0
                "
                @click="nextPage()"
                variant="tertiary"
                size="small"
              >
                <template #text
                  ><b-icon-chevron-right></b-icon-chevron-right
                ></template>
              </TheButton>
            </div>
          </nav>
        </CardFooter>
      </template>
    </TheCard>
  </template>
</template>

<script>
import TheCard from "../../components-v2/ui/layout/TheCard.vue";
import CardHeader from "../../components-v2/ui/layout/CardHeader.vue";
import CardBody from "../../components-v2/ui/layout/CardBody.vue";
import CardFooter from "../../components-v2/ui/layout/CardFooter.vue";
import TheModal from "../../components-v2/ui/modals/TheModal.vue";
import WideModal from "../../components-v2/ui/modals/WideModal.vue";
import NewRecipientForm from "../../components-v2/ui/modals/NewRecipientForm.vue";
import EditRecipientForm from "../../components-v2/ui/modals/EditRecipientForm.vue";
import TheButton from "../../components-v2/ui/actions/TheButton.vue";
import TheLink from "../../components-v2/ui/actions/TheLink.vue";
import EmptyState from "../../components-v2/ui/feedback/EmptyState.vue";
import DropdownMenu from "../../components-v2/ui/forms/DropdownMenu.vue";
import RecipientsGroupsButtonDropdown from "../../components-v2/ui/elements/dropdown/RecipientsGroupsButtonDropdown.vue";
import DeleteData from "../ui/modals/DeleteData.vue";
import BulkEditData from "../ui/modals/BulkEditData.vue";
import PageSection from "../../components-v2/ui/layout/PageSection.vue";
import DataTableSkeleton from "../../components-v2/ui/skeletons/DataTableSkeleton.vue";
import PulseAnimation from "../../components-v2/ui/feedback/PulseAnimation.vue";
import ModalBody from "../../components-v2/ui/layout/ModalBody.vue";
import ButtonDropdown from "../../components-v2/ui/elements/dropdown/ButtonDropdown.vue";
import IconDropdown from "../../components-v2/ui/elements/dropdown/IconDropdown.vue";
import GroupForm from "../../components-v2/ui/modals/create/GroupForm.vue";
import TheTooltip from "../../components-v2/ui/forms/TheTooltip.vue";
import BlockStack from "../../components-v2/ui/layout/BlockStack.vue";
import TheBox from "../../components-v2/ui/layout/TheBox.vue";

import { deleteRecipients } from "@/services/recipientService";

export default {
  name: "RecipientProfilesTable",
  components: {
    TheBox,
    BlockStack,
    TheTooltip,
    GroupForm,
    RecipientsGroupsButtonDropdown,
    IconDropdown,
    ButtonDropdown,
    BulkEditData,
    DeleteData,
    NewRecipientForm,
    EditRecipientForm,
    TheModal,
    DropdownMenu,
    TheLink,
    EmptyState,
    CardBody,
    TheCard,
    CardHeader,
    TheButton,
    CardFooter,
    PageSection,
    DataTableSkeleton,
    PulseAnimation,
    ModalBody,
    WideModal,
  },
  emits: [
    "setCampaignRecipients",
    "update:isModalVisible",
    "updateSegmentRecipientCount",
    "updateGroupRecipientsCount",
    "reloadData",
  ],
  props: {
    campaignView: {
      type: Boolean,
      default: false,
    },
    recipientsGroupDetailsView: {
      type: Boolean,
    },
    groupId: {
      type: String,
      default: null,
    },
    segmentId: {
      type: String,
      default: null,
    },
    segmentFilter: {
      type: Object,
      default: null,
    },
    campaignId: {
      type: String,
    },
    dashboardView: {
      type: Boolean,
      default: false,
    },
    isModalVisible: {
      type: Boolean,
      default: false,
    },
    sharedCampaignData: {
      type: Array,
      default: () => [],
    },
    totalRecipientsCount: {
      type: Number,
      default: 1,
      required: false,
    },
    campaignRecipients: {
      type: Array,
    },
    viewCampaignRecipientsSelection: {
      type: Boolean,
    },
    existingGroupMemberIds: {
      type: Array,
      default: null,
    },
  },
  watch: {
    isModalVisible(newVal) {
      if (newVal) {
        this.toggleNewRecipientModal();
      }
    },
    existingGroupMemberIds(newVal) {
      if (newVal) {
        this.checkForRecipientData();
      }
    },
    selectedGroup(newVal) {
      if (newVal) {
        this.listRecipientsByGroupId();
      }
    },
  },
  created() {
    this.checkForRecipientData();
  },
  data() {
    return {
      newRecipient: {},
      selectedRecipient: {},
      searchQuery: {
        active: false,
        value: "",
      },
      lifecycleStages: [
        { lifecycleStage: "Center of Influence", count: 0 },
        { lifecycleStage: "Customer", count: 0 },
        { lifecycleStage: "Warm Lead", count: 0 },
        { lifecycleStage: "Cold Lead", count: 0 },
        { lifecycleStage: "Unlisted Lead", count: 0 },
      ],
      recipients: [],
      selectedRecipients: [],
      recipientIdsToRemove: [],
      selectedGroup: null,
      viewMyRecordsOnly: true,
      showNewRecipientModal: false,
      showRecipientDetailsModal: false,
      recipientsAreLoading: false,
      isAddingRecipient: false,
      userHasRecipients: false,
      checkingIfUserHasRecipients: false,
      recipientIdsToDelete: null,
      recipientIdsToEdit: null,
      recipientIsReadyToSave: false,
      showDeleteModal: false,
      showRemoveModal: false,
      showBulkEditModal: false,
      viewLifecycleDistribution: false,
      viewAllRecipients: true,
      viewRecipientsByLifecycleStage: false,
      campaignRecipientsSelectionUpdated: false,
      showNewGroupModal: false,
      count: 0,
      pageSize: 200,
      currentPage: 1,
      sortedValue: "firstName",
    };
  },
  computed: {
    cardHeaderTitle() {
      if (this.dashboardView) {
        return "Recipients";
      } else {
        return "";
      }
    },
    recipientData() {
      return this.dashboardView ? this.sharedCampaignData : this.recipients;
    },
    isLastPage() {
      return (
        this.count <= this.pageSize ||
        this.currentPage === Math.ceil(this.count) ||
        this.recipients.length < this.pageSize ||
        this.count / this.currentPage === this.pageSize
      );
    },
    userId() {
      return this?.$store?.state?.auth?.user?._id;
    },
    userClient() {
      return this?.$store?.state?.auth?.user?.client;
    },
    recipientGiftingLead() {
      return (recipient) => {
        if (
          recipient?.metadata?.giftingLeads &&
          recipient?.metadata?.giftingLeads[0] ===
            this?.$store?.state?.auth?.user?._id
        ) {
          return true;
        }
      };
    },
    idsToEdit() {
      return this.recipientIdsToEdit.map((recipient) => recipient._id);
    },
    idsToDelete() {
      return this.recipientIdsToDelete.map((recipient) => recipient._id);
    },
    idsToRemove() {
      return this.recipientIdsToRemove.map((recipient) => recipient._id) || [];
    },
    tags() {
      return (recipient) => {
        if (
          Array.isArray(recipient?.contact?.metadata?.lifecycleStage) &&
          recipient?.contact?.metadata?.lifecycleStage.length === 1
        ) {
          return recipient?.contact?.metadata?.lifecycleStage[0];
        }
        return recipient?.contact?.metadata?.lifecycleStage?.join(", ") || "";
      };
    },
    combinedUniqueIds() {
      const uniqueIds = [
        ...new Set([...this.existingGroupMemberIds, ...this.idsToRemove]),
      ];
      return uniqueIds.map((id) => ({
        doc: id,
        metadata: {},
      }));
    },
    notificationTypes() {
      // Access namespaced getter
      return this.$store.getters["alerts/getAlertTypes"];
    },
  },
  methods: {
    /** handle deleting selected recipients */
    async confirmDelete() {
      try {
        const response = await deleteRecipients(this.idsToDelete);
        this.handleSuccess(response, "deleteRecipients");
      } catch (error) {
        console.log("recipient was NOT deleted", error);
        this.handleErrorFromAPI(
          error,
          "There was issue processing your delete request. Please try again."
        );
      } finally {
        this.showDeleteModal = false;
      }
    },
    async confirmRemove() {
      /**@type {import('@boldxchange/sdk/defs').Client}*/
      const api = await this.getBoldClient();
      const recipientsIds = this.removeIds(
        this.existingGroupMemberIds,
        this.idsToRemove
      );
      const groupPatch = {
        members: recipientsIds,
      };
      const groupId = this.groupId;

      try {
        const groupsRes = await api.upsertGroup(null, {
          filter: { _id: groupId },
          patch: groupPatch,
        });

        this.handleSuccess(groupsRes, "upsertGroup");
      } catch (error) {
        this.handleError(error);
        console.log("group was NOT upserted", error);
      }
    },
    removeIds(existingGroupMemberIds, idsToRemove) {
      // Convert idsToRemove array to a Set for efficient lookups
      const idsToRemoveSet = new Set(idsToRemove);

      // Filter out the IDs that are in the idsToRemoveSet
      const uniqueIds = existingGroupMemberIds.filter(
        (id) => !idsToRemoveSet.has(id)
      );

      // Map the unique IDs to the specified structure
      return uniqueIds.map((id) => ({
        doc: id,
        metadata: {},
      }));
    },
    toggleDeleteModal(recipient) {
      this.selectedRecipients.push(recipient);
      this.recipientIdsToDelete = this.selectedRecipients;
      this.showDeleteModal = !this.showDeleteModal;
    },
    toggleRemoveModal(recipient) {
      this.recipientIdsToRemove = [recipient];
      this.showRemoveModal = !this.showRemoveModal;
    },
    handleBulkDelete(recipients) {
      this.recipientIdsToDelete = recipients;
      this.showDeleteModal = !this.showDeleteModal;
    },

    /** handle viewing and editing recipient details */
    toggleRecipientDetailsModal(recipient) {
      this.selectedRecipient = recipient;
      this.showRecipientDetailsModal = !this.showRecipientDetailsModal;
    },

    /** handle bulk editing recipients */
    async updateGroupMembers(groupData) {
      /**@type {import('@boldxchange/sdk/defs').Client}*/
      const api = await this.getBoldClient();

      const groupPatch = {
        members: groupData.recipientsIds,
      };

      console.log("groupData.recipientsIds: ", groupData.recipientsIds);
      try {
        const response = await api.upsertGroup(null, {
          filter: { _id: groupData.groupId },
          patch: groupPatch,
        });
        this.handleSuccess(response, "updateGroupMembers");
      } catch (error) {
        console.log("recipient was NOT edited", error);
        this.handleErrorFromAPI(
          error,
          "There was issue processing your edit request. Please try again."
        );
      } finally {
        this.showBulkEditModal = false;
      }
    },
    handleBulkEdit(recipients) {
      this.recipientIdsToEdit = recipients;
      this.showBulkEditModal = !this.showBulkEditModal;
    },
    closeModal() {
      this.showDeleteModal = false;
      this.showRemoveModal = false;
      this.showNewRecipientModal = false;
      this.showRecipientDetailsModal = false;
      this.showNewGroupModal = false;
      (this.showBulkEditModal = false),
        this.$emit("update:isModalVisible", false);
    },

    /** handle recipient selection */
    selectAllRecipients(isChecked) {
      this.selectedRecipients = isChecked ? this.recipients.slice() : [];
    },
    isRecipientSelected(recipientId) {
      return this.selectedRecipients.some((r) => r.id === recipientId);
    },
    toggleRecipientSelection(recipient) {
      const index = this.selectedRecipients.findIndex(
        (r) => r.id === recipient.id
      );
      if (index > -1) {
        this.selectedRecipients.splice(index, 1);
      } else {
        this.selectedRecipients.push(recipient);
      }
    },

    /** load data */
    async checkForRecipientData(page) {
      this.checkingIfUserHasRecipients = true;
      try {
        await this.checkIfUserHasRecipients(page);
        // if (this.count) {
        //   await Promise.all([
        //     this.loadWarmLeads(),
        //     this.loadColdLeads(),
        //     this.loadCenterOfInfluenceLeads(),
        //     this.loadCustomerLeads(),
        //     this.loadUnlistedLeads(),
        //   ]);
        // }
      } catch (error) {
        this.handleErrorFromAPI(
          error,
          "We couldn't load your recipients data. Please reload the page and try again."
        );
      } finally {
        this.checkingIfUserHasRecipients = false;
      }
    },
    async checkIfUserHasRecipients(page) {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();

      this.currentPage = page === undefined ? this.currentPage : page;
      this.checkingIfUserHasRecipients = true;

      let filter = {};

      // Constructing filter based on conditions
      if (this.segmentId) {
        console.log("segmentFilter: ", this.segmentFilter);
        filter = this.segmentFilter.filter;
        // alert()
      } else if (this.groupId) {
        // alert();
        filter = {
          "metadata.giftingLeads": this.viewMyRecordsOnly
            ? { $in: [this.userId] }
            : { $ne: [this.userId] },
          "groups._id": { $eq: this.groupId },
        };
      } else {
        filter = {
          "metadata.giftingLeads": this.viewMyRecordsOnly
            ? { $in: [this.userId] }
            : { $ne: [this.userId] },
        };
      }

      if (this.searchQuery.value !== "") {
        filter.$or = [
          { firstName: { $regex: this.searchQuery.value } },
          { lastName: { $regex: this.searchQuery.value } },
          { "contact.email": { $regex: this.searchQuery.value } },
          { "contact.phone": { $regex: this.searchQuery.value } },
          { "contact.company": { $regex: this.searchQuery.value } },
          { "contact.title": { $regex: this.searchQuery.value } },
          {
            "contact.metadata.lifecycleStage": {
              $regex: this.searchQuery.value,
            },
          },
        ];
      }

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "groups campaigns",
      };

      const requestBody = { filter };

      try {
        const response = await api.listRecipients(queryParameters, requestBody);
        const count = response.data.result.count;

        this.count = count;
        this.userHasRecipients = count > 0;
        this.recipients = response.data.result.records;
        if (this.groupId) {
          this.$emit('updateGroupRecipientsCount', count);
          // alert()
        }
      } catch (error) {
        console.error("recipients did not load", error);
      } finally {
        this.checkingIfUserHasRecipients = false;
      }
    },

    /** paginate list of recipients */
    async nextPage() {
      /**
       * @type {import('@boldxchange/sdk/defs').Client}
       */
      const api = await this.getBoldClient();
      this.recipientsAreLoading = true;
      this.currentPage++;

      let requestBody = {};
      let filter = {};
      filter = {
        ...(!this.isBoldXchangeAdmin && {
          "metadata.giftingLeads": { $in: [this.userId] },
        }),
        ...(this.isBoldXchangeAdmin &&
          !this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $ne: [this.userId] },
          }),
        ...(this.isBoldXchangeAdmin &&
          this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $in: [this.userId] },
          }),
      };

      const groupFilter = this.selectedGroup
        ? {
            "groups._id": { $eq: this.selectedGroup._id },
          }
        : {};

      const searchQueryFilter = this.searchQuery.value
        ? {
            $or: [
              { firstName: { $regex: this.searchQuery.value } },
              { lastName: { $regex: this.searchQuery.value } },
              { "contact.email": { $regex: this.searchQuery.value } },
              {
                "contact.metadata.lifecycleStage": {
                  $regex: this.searchQuery.value,
                },
              },
            ],
          }
        : {};

      filter = {
        ...filter,
        ...groupFilter,
        ...searchQueryFilter,
      };

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "groups campaigns",
      };

      let selection = "firstName lastName contact metadata campaigns";

      requestBody = {
        filter: filter,
        selection: selection,
      };

      try {
        const response = await api.listRecipients(queryParameters, requestBody);

        this.recipients = response.data.result.records;
        this.count = response.data.result.count;
      } catch (error) {
        this.handleErrorFromAPI(
          error,
          "We couldn't load your recipients data. Please try again."
        );
      } finally {
        this.recipientsAreLoading = false;
      }
    },
    async previousPage() {
      /**
       * @type {import('@boldxchange/sdk/defs').Client}
       */
      const api = await this.getBoldClient();
      this.recipientsAreLoading = true;
      this.currentPage--;

      let requestBody = {};
      let filter = {};
      filter = {
        ...(!this.isBoldXchangeAdmin && {
          "metadata.giftingLeads": { $in: [this.userId] },
        }),
        ...(this.isBoldXchangeAdmin &&
          !this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $ne: [this.userId] },
          }),
        ...(this.isBoldXchangeAdmin &&
          this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $in: [this.userId] },
          }),
      };

      const groupFilter = this.selectedGroup
        ? {
            "groups._id": { $eq: this.selectedGroup._id },
          }
        : {};

      const searchQueryFilter = this.searchQuery.value
        ? {
            $or: [
              { firstName: { $regex: this.searchQuery.value } },
              { lastName: { $regex: this.searchQuery.value } },
              { "contact.email": { $regex: this.searchQuery.value } },
              {
                "contact.metadata.lifecycleStage": {
                  $regex: this.searchQuery.value,
                },
              },
            ],
          }
        : {};

      filter = {
        ...filter,
        ...groupFilter,
        ...searchQueryFilter,
      };

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "groups campaigns",
      };

      let selection = "firstName lastName contact metadata campaigns";

      requestBody = {
        filter: filter,
        selection: selection,
      };

      try {
        const response = await api.listRecipients(queryParameters, requestBody);

        this.recipients = response.data.result.records;
        this.count = response.data.result.count;
      } catch (error) {
        this.handleErrorFromAPI(
          error,
          "We couldn't load your recipients data. Please try again."
        );
      } finally {
        this.recipientsAreLoading = false;
      }
    },

    async listRecipientsByGroupId() {
      /**
       * @type {import('@boldxchange/sdk/defs').Client}
       */
      const api = await this.getBoldClient();

      this.currentPage = 1;
      this.recipientsAreLoading = true;
      let requestBody = {};
      let filter = {};
      filter = {
        ...(!this.isBoldXchangeAdmin && {
          "metadata.giftingLeads": { $in: [this.userId] },
        }),
        ...(this.isBoldXchangeAdmin &&
          !this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $ne: [this.userId] },
          }),
        ...(this.isBoldXchangeAdmin &&
          this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $in: [this.userId] },
          }),
      };

      const groupFilter = this.selectedGroup
        ? {
            "groups._id": { $eq: this.selectedGroup._id },
          }
        : {};

      const searchQueryFilter = this.searchQuery.active
        ? {
            $or: [
              { firstName: { $regex: this.searchQuery.value } },
              { lastName: { $regex: this.searchQuery.value } },
              { "contact.email": { $regex: this.searchQuery.value } },
              {
                "contact.metadata.lifecycleStage": {
                  $regex: this.searchQuery.value,
                },
              },
            ],
          }
        : {};

      filter = {
        ...filter,
        ...groupFilter,
        ...searchQueryFilter,
      };

      /////////

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "groups campaigns",
      };

      let selection = "firstName lastName contact metadata campaigns";

      requestBody = {
        filter: filter,
        selection: selection,
      };

      try {
        const response = await api.listRecipients(queryParameters, requestBody);
        this.recipients = response.data.result.records;
        const count = response.data.result.count;
        this.count = count;
      } catch (error) {
        this.handleErrorFromAPI(
          error,
          "We couldn't load your recipients data. Please reload the page and try again."
        );
      } finally {
        this.recipientsAreLoading = false;
      }
    },
    /** handle resetting recipient list */
    resetFilters() {
      this.searchQuery.value = "";
      this.searchQuery.active = false;
      this.currentPage = 1;
      if (this.selectedGroup) {
        this.listRecipientsByGroupId();
      } else {
        this.loadRecipients();
      }
    },
    clearGroupSelection() {
      this.searchQuery.value = "";
      this.searchQuery.active = false;
      this.currentPage = 1;
      this.selectedGroup = null;
      this.checkForRecipientData();
    },
    async loadRecipients(page) {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();
      this.recipientsAreLoading = true;

      this.currentPage = page === undefined ? this.currentPage : page;

      let requestBody = {};
      let filter = {};
      filter = {
        ...(!this.isBoldXchangeAdmin && {
          "metadata.giftingLeads": { $in: [this.userId] },
        }),
        ...(this.isBoldXchangeAdmin &&
          !this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $ne: [this.userId] },
          }),
        ...(this.isBoldXchangeAdmin &&
          this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $in: [this.userId] },
          }),
      };

      const searchQueryFilter = this.searchQuery.value
        ? {
            $or: [
              { firstName: { $regex: this.searchQuery.value } },
              { lastName: { $regex: this.searchQuery.value } },
              { "contact.email": { $regex: this.searchQuery.value } },
              {
                "contact.metadata.lifecycleStage": {
                  $regex: this.searchQuery.value,
                },
              },
            ],
          }
        : {};

      filter = {
        ...filter,
        ...searchQueryFilter,
      };

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "groups campaigns",
      };

      let selection = "firstName lastName contact metadata campaigns";

      requestBody = {
        filter: filter,
        selection: selection,
      };

      try {
        const response = await api.listRecipients(queryParameters, requestBody);

        this.recipientsAreLoading = false;
        this.recipients = response.data.result.records;
        this.count = response.data.result.count;
      } catch (error) {
        this.recipientsAreLoading = false;
        console.log("recipients did not load", error);
      }
    },

    /**handle listing recipients by search query*/
    handleSearchQuery() {
      this.searchQuery.active = true;
      if (this.selectedGroup) {
        this.listRecipientsByGroupId();
      } else {
        this.getRecipientsBySearchQuery();
      }
    },
    createSearchQuery() {
      if (!this.searchQuery.active) return {};

      const searchValues = this.searchQuery.value.split(" ");
      const userFields = ["firstName", "lastName", "contact.email"];

      const searchConditions = searchValues.flatMap((value) => [
        ...userFields.map((field) => ({
          [field]: { $regex: value, $options: "i" },
        })),
        {
          "contact.metadata.lifecycleStage": {
            $regex: value,
          },
        },
      ]);

      return {
        $or: searchConditions,
      };
    },
    async getRecipientsBySearchQuery(page) {
      /**
       * @type {import('@boldxchange/sdk/defs').Client}
       */
      const api = await this.getBoldClient();
      this.recipientsAreLoading = true;
      this.currentPage = page === undefined ? 1 : page;

      let requestBody = {};
      let filter = {};
      filter = {
        ...(!this.isBoldXchangeAdmin && {
          "metadata.giftingLeads": { $in: [this.userId] },
        }),
        ...(this.isBoldXchangeAdmin &&
          !this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $ne: [this.userId] },
          }),
        ...(this.isBoldXchangeAdmin &&
          this.viewMyRecordsOnly && {
            "metadata.giftingLeads": { $in: [this.userId] },
          }),
      };
      let query = this.createSearchQuery();

      filter = { ...filter, ...query };

      const queryParameters = {
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
        sort: this.sortedValue,
        expand: "groups campaigns",
      };

      let selection = "firstName lastName contact metadata campaigns";

      requestBody = {
        filter: filter,
        selection: selection,
      };

      try {
        const response = await api.listRecipients(queryParameters, requestBody);
        this.recipients = response.data.result.records;
        this.count = response.data.result.count;
      } catch (error) {
        this.handleErrorFromAPI(
          error,
          "We couldn't load your recipients data. Please try again."
        );
      } finally {
        this.recipientsAreLoading = false;
      }
    },

    /** handle adding recipient to campaign */
    setCampaignRecipients() {
      this.$emit("setCampaignRecipients", this.selectedRecipients);
      this.selectedRecipients = [];
      this.campaignRecipientsSelectionUpdated = true;
      setTimeout(() => {
        this.campaignRecipientsSelectionUpdated = false;
      }, 4000);
    },

    /** dropdown menu items and actions */
    getDropdownItems(recipient) {
      this.selectedRecipient = recipient;
      if (this.recipientsGroupDetailsView) {
        return [
          {
            label: "Remove",
            isDeleteAction: true,
            action: () => this.toggleRemoveModal(recipient),
          },
        ];
      } else {
        return [
          {
            label: "Edit",
            isDeleteAction: false,
            action: () => this.toggleRecipientDetailsModal(recipient),
          },
          {
            label: "Delete",
            isDeleteAction: true,
            action: () => this.toggleDeleteModal(recipient),
          },
        ];
      }
    },
    handleRemovingGroupRecipients(selectedRecipients) {
      this.recipientIdsToRemove = selectedRecipients;
      this.showRemoveModal = !this.showRemoveModal;
    },
    bulkDropdownItems() {
      if (this.recipientsGroupDetailsView) {
        return [
          {
            label: "Remove from group",
            isDeleteAction: true,
            action: () =>
              this.handleRemovingGroupRecipients(this.selectedRecipients),
          },
        ];
      } else {
        return [
          {
            label: "Create new group",
            sublabel: "Create a new group with selected recipients",
            action: () => this.toggleNewGroupModal(),
          },
          {
            label: "Add to existing group",
            sublabel: "Update an existing group with selected recipients",
            action: () => this.handleBulkEdit(this.selectedRecipients),
          },
          {
            label: "Delete",
            isDeleteAction: true, // This item is a delete action
            action: () => this.handleBulkDelete(this.selectedRecipients),
          },
        ];
      }
    },
    addOrImportDropdownItems() {
      return [
        {
          label: "Quick add",
          isDeleteAction: false,
          action: () => this.toggleNewRecipientModal(),
        },
        {
          label: "Import CSV",
          isRouterLink: true,
          to: `/recipients/import/${this.campaignId}`,
        },
      ];
    },
    setRecordsUserRecordsToView(value) {
      this.selectedRecipients = [];
      this.viewMyRecordsOnly = value;
      this.searchQuery.value = "";
      this.searchQuery.active = false;
      this.checkForRecipientData();
    },

    reloadList() {
      const page = this.currentPage;
      if (this.searchQuery.value) {
        this.handleSearchQuery();
      } else {
        this.checkForRecipientData(page);
      }
    },

    /** quick edits */
    activateQuickEdit() {
      // Trigger mutation or action to set quickEditActive to true
      this.$store.dispatch("orders/activateQuickEdit");
    },
    deactivateQuickEdit() {
      // Trigger mutation or action to set quickEditActive to false
      this.$store.dispatch("orders/deactivateQuickEdit");
    },

    /** modals */
    toggleNewRecipientModal() {
      this.showNewRecipientModal = !this.showNewRecipientModal;
    },
    toggleNewGroupModal() {
      this.showNewGroupModal = !this.showNewGroupModal;
    },
    handleNewContact(recipient) {
      this.showNewRecipientModal = false;
      this.handleSuccess(recipient, "handleNewRecipient");
    },
    handleGroupSelection(group) {
      this.selectedGroup = group;
      this.listRecipientsByGroupId();
    },

    /** handle success and errors */
    handleSuccess(response, api) {
      if (this.campaignView) {
        // let newRecipientEmail = response.contact.email;
        // this.searchQuery.value = newRecipientEmail;
        // this.searchQuery.active = true;
        // this.userHasRecipients = true;
        // this.getRecipientsBySearchQuery();
        this.$emit("setCampaignRecipients", [response]);
        this.campaignRecipientsSelectionUpdated = true;
        setTimeout(() => {
          this.campaignRecipientsSelectionUpdated = false;
        }, 4000);
        // this.reloadList();
        this.$store.dispatch("alerts/showAlert", {
          type: this.notificationTypes.SUCCESS,
          message: "Recipient successfully created.",
          duration: 4000,
          id: Date.now(),
        });
        return;
      }

      if (api === "handleNewRecipient") {
        let newRecipientEmail = response.contact.email;
        this.searchQuery.value = newRecipientEmail;
        this.searchQuery.active = true;
        this.userHasRecipients = true;
        this.getRecipientsBySearchQuery();
        this.$store.dispatch("alerts/showAlert", {
          type: this.notificationTypes.SUCCESS,
          message: "Recipient successfully created.",
          duration: 4000,
          id: Date.now(),
        });
      } else if (api === "deleteRecipients") {
        let res = response;
        this.selectedRecipients = [];
        this.$store.dispatch("alerts/showAlert", {
          type: this.notificationTypes.SUCCESS,
          message: "Selected recipients were successfully deleted.",
          duration: 4000,
          id: Date.now(),
        });
        this.loadRecipients();
      } else if (api === "bulkUpdateRecipients") {
        let res = response;
        this.selectedRecipients = [];
        this.$store.dispatch("alerts/showAlert", {
          type: this.notificationTypes.SUCCESS,
          message: "Selected recipients were successfully edited.",
          duration: 4000,
          id: Date.now(),
        });
        this.loadRecipients();
      } else if (api === "updateGroupMembers") {
        const groupId = response.data.result._id;
        this.selectedRecipients = [];
        this.$store.dispatch("alerts/showAlert", {
          type: this.notificationTypes.SUCCESS,
          message: "Recipients were added to group.",
          duration: 4000,
          id: Date.now(),
        });
        this.$router.push({
          name: "RecipientGroupDetails",
          params: { groupId },
        });
      } else if (api === "upsertGroup") {
        this.selectedRecipients = [];
        this.$store.dispatch("alerts/showAlert", {
          type: this.notificationTypes.SUCCESS,
          message: "Recipients were removed from group.",
          duration: 4000,
          id: Date.now(),
        });
        this.$emit("reloadData");
        this.showRemoveModal = false;
      } else {
        this.showNewRecipientModal = false;
        this.loadRecipients();
      }
    },

    handleErrorFromAPI(error, customMessage = null) {
      let errorMessage = "Something went wrong. Please try again."; // Default message

      // If a custom message is provided, prioritize it
      if (customMessage) {
        errorMessage = customMessage;
      } else if (error?.response) {
        // Process API error message if customMessage is not provided
        if (
          error?.response?.data &&
          error?.response?.data?.error &&
          error?.response?.data?.error?.source?.errors
        ) {
          const sourceErrors = error.response.data.error.source.errors;
          const firstErrorKey = Object.keys(sourceErrors)[0];
          errorMessage = sourceErrors[firstErrorKey].message;
        } else {
          errorMessage =
            error.response.data.message || "An unknown error occurred.";
        }
      } else if (error?.request) {
        errorMessage =
          "No response was received from the server. Please check your internet connection.";
      } else {
        errorMessage = "An error occurred with your request. Please try again.";
      }

      //Dispatch the error message as before
      this.$store.dispatch("alerts/showAlert", {
        type: this.notificationTypes.ERROR,
        message: errorMessage,
        duration: 6000,
        id: Date.now(),
      });

      // this.reloadList();
      console.error(errorMessage); // Keep for debugging purposes
    },
  },
};
</script>
